/**
 * Colours of the image viewer: its background, the close / previous / next
 * controls with the image counter, and the image title.
 *
 * The theme sets them for every page (`colors.viewer*`, the title on its
 * `media-title` module). A page can set its own in `page.viewerLook`, which
 * its subpages inherit key by key.
 */

import { getParentId } from '../utils/pagePaths.js';
import { themeValueAtLayer } from './document.js';
import { colorWithAlpha } from './colors.js';
import { breakpointKey, defaultHeaderModules } from './header-modules.js';

export const VIEWER_THEME_PATHS = {
    background: 'colors.viewerBackground',
    controls: 'colors.viewerControls',
    controlsBackground: 'colors.viewerControlsBackground',
};

export const VIEWER_LOOK_KEYS = ['background', 'controls', 'controlsBackground', 'title', 'titleBackground'];

const COLOR_VALUE = /^[#a-z0-9(),.%\s-]{1,64}$/i;

function cleanColor(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed && COLOR_VALUE.test(trimmed) ? trimmed : null;
}

function cleanOpacity(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : null;
}

/** Viewer parts a page can place, show or hide on its own, per screen size. */
export const VIEWER_MODULE_IDS = ['viewer-logo', 'icon-close', 'icon-prev', 'icon-next', 'media-frame', 'media-title'];
const MODULE_LAYERS = ['desktop', 'tablet', 'phone'];
const GLYPH_VALUE = /^[\w:.-]{1,120}$/;

function plainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value);
}

function cleanModules(value) {
    if (!plainObject(value)) return null;
    const out = {};
    VIEWER_MODULE_IDS.forEach((id) => {
        const entry = value[id];
        if (!plainObject(entry)) return;
        const clean = {};
        MODULE_LAYERS.forEach((layer) => {
            if (plainObject(entry[layer])) clean[layer] = { ...entry[layer] };
        });
        if (typeof entry.glyph === 'string' && GLYPH_VALUE.test(entry.glyph)) clean.glyph = entry.glyph;
        if (Object.keys(clean).length) out[id] = clean;
    });
    return Object.keys(out).length ? out : null;
}

/** Only the known keys with usable values; null when nothing is left. */
export function cleanViewerLook(value) {
    if (!plainObject(value)) return null;
    const out = {};
    VIEWER_LOOK_KEYS.forEach((key) => {
        const color = cleanColor(value[key]);
        if (color) out[key] = color;
    });
    const opacity = cleanOpacity(value.titleBackgroundOpacity);
    if (opacity !== null) out.titleBackgroundOpacity = opacity;
    if (typeof value.logoFileId === 'string' && value.logoFileId.trim()) out.logoFileId = value.logoFileId.trim();
    const modules = cleanModules(value.modules);
    if (modules) out.modules = modules;
    return Object.keys(out).length ? out : null;
}

/** Fill `out` with whatever `look` sets that `out` does not yet have. */
function fillViewerLook(out, look) {
    const own = cleanViewerLook(look);
    if (!own) return;
    Object.entries(own).forEach(([key, value]) => {
        if (key === 'modules') {
            out.modules = out.modules || {};
            Object.entries(value).forEach(([id, entry]) => {
                const merged = { ...(out.modules[id] || {}) };
                Object.entries(entry).forEach(([part, box]) => {
                    if (!(part in merged)) merged[part] = box;
                });
                out.modules[id] = merged;
            });
        } else if (!(key in out)) {
            out[key] = value;
        }
    });
}

/** Looks nearest first, combined key by key; null when none sets anything. */
export function combineViewerLooks(...looks) {
    const out = {};
    looks.forEach((look) => fillViewerLook(out, look));
    return Object.keys(out).length ? out : null;
}

/** This page's viewer look, with anything it leaves out taken from the nearest parent that sets it. */
export function pageViewerLook(page, pages) {
    const out = {};
    let current = page || null;
    const seen = new Set();
    while (current && typeof current === 'object') {
        fillViewerLook(out, current.viewerLook);
        const parentId = getParentId(current);
        if (!parentId || !pages?.[parentId] || seen.has(parentId)) break;
        seen.add(parentId);
        current = pages[parentId];
    }
    return Object.keys(out).length ? out : null;
}

/** The theme's header modules with a page's own viewer boxes and icons on top. */
export function viewerModules(modules, look) {
    if (!look?.modules) return modules;
    const out = { ...(modules || defaultHeaderModules()) };
    Object.entries(look.modules).forEach(([id, own]) => {
        const entry = { ...(out[id] || defaultHeaderModules()[id] || {}) };
        MODULE_LAYERS.forEach((layer) => {
            if (own[layer]) entry[layer] = { ...(entry[layer] || entry.desktop || {}), ...own[layer] };
        });
        if (own.glyph) {
            MODULE_LAYERS.forEach((layer) => {
                if (entry[layer] || layer === 'desktop') entry[layer] = { ...(entry[layer] || {}), glyph: own.glyph };
            });
        }
        out[id] = entry;
    });
    return out;
}

function withOwnModule(page, id, change) {
    const look = page?.viewerLook || {};
    const modules = { ...(look.modules || {}) };
    const entry = change({ ...(modules[id] || {}) });
    if (entry && Object.keys(entry).length) modules[id] = entry;
    else delete modules[id];
    return { ...page, viewerLook: cleanViewerLook({ ...look, modules }) };
}

/** The page with its own box for one viewer part on one screen size. */
export function pageWithViewerBox(page, id, layer, box) {
    return withOwnModule(page, id, (entry) => ({ ...entry, [breakpointKey(layer)]: box }));
}

/** The page with its own icon for one viewer control, on every screen size. */
export function pageWithViewerGlyph(page, id, glyph) {
    return withOwnModule(page, id, (entry) => {
        const next = { ...entry };
        if (glyph) next.glyph = glyph;
        else delete next.glyph;
        return next;
    });
}

/** The page with one viewer part back on the setting for all pages (or its parent's). */
export function pageWithoutViewerModule(page, id) {
    return withOwnModule(page, id, () => null);
}

/** The theme as the viewer on this page draws it. */
export function viewerTheme(theme, look) {
    if (!theme || (!look?.modules && !look?.logoFileId)) return theme;
    const logo = theme.brand?.logo;
    return {
        ...theme,
        header: { ...(theme.header || {}), modules: viewerModules(theme.header?.modules, look) },
        ...(look.logoFileId ? {
            brand: {
                ...(theme.brand || {}),
                logo: { ...(logo || {}), mediaFileId: look.logoFileId, slideshowFileId: look.logoFileId },
            },
        } : {}),
    };
}

function themeLayer(layer) {
    return !layer || layer === 'desktop' ? 'base' : layer;
}

/** The theme's viewer colours on one screen size. */
export function themeViewerLook(theme, layer) {
    const out = {};
    Object.entries(VIEWER_THEME_PATHS).forEach(([key, path]) => {
        const color = cleanColor(themeValueAtLayer(theme, path, themeLayer(layer)));
        if (color) out[key] = color;
    });
    return out;
}

/** Custom properties for the viewer's root element. */
export function viewerLookVars(look) {
    if (!look) return {};
    const vars = {};
    if (look.background) vars['--color-background'] = look.background;
    if (look.controls) vars['--viewer-controls'] = look.controls;
    if (look.controlsBackground) vars['--viewer-controls-background'] = look.controlsBackground;
    return vars;
}

/** The page's title colours over the theme's title box. */
export function titleWithPageLook(box, look) {
    const opacity = look?.titleBackgroundOpacity;
    if (!look?.title && !look?.titleBackground && opacity == null) return box;
    return {
        ...(box || {}),
        ...(look.title ? { color: look.title } : {}),
        ...(look.titleBackground ? { background: look.titleBackground } : {}),
        ...(opacity != null ? { backgroundOpacity: opacity } : {}),
    };
}

/** Title colours a page sets when the theme leaves the title box alone. */
export function pageTitleStyle(look, box) {
    if (!look?.title && !look?.titleBackground) return null;
    const opacity = look.titleBackgroundOpacity
        ?? (box?.backgroundOpacity != null && box.backgroundOpacity !== '' ? box.backgroundOpacity : 1);
    const background = look.titleBackground ? colorWithAlpha(look.titleBackground, opacity) : null;
    return {
        ...(look.title ? { color: look.title } : {}),
        ...(background ? { backgroundColor: background, padding: '0.35em 0.65em' } : {}),
    };
}

/** Control colours for elements that sit in the viewer. */
export const VIEWER_CONTROL_STYLE = {
    color: 'var(--viewer-controls, var(--color-text))',
};

export const VIEWER_CONTROL_BACKDROP = {
    backgroundColor: 'var(--viewer-controls-background, var(--color-background))',
};
